import React, { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { requireAdmin } from "@/auth/requireAdmin";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { Download, RefreshCw, Search, X } from "lucide-react";
import { PageHeader } from "@/components/navigation/page-header";
import { Button, Card, CardContent, PageContent } from "beproduct-ui";
import Fuse, { type FuseResultMatch } from "fuse.js";
import { api } from "@/lib/api";
import {
  SchemaDiagram,
  type SchemaColumn,
  type SchemaRelationship,
  type SchemaTable,
} from "@/components/schema-diagram";

type SchemaCategory = "raw" | "pivot" | "schema" | "other";

interface SchemaIndexEntry {
  name: string;
  category: SchemaCategory;
  group: string;
  column_count: number;
  row_estimate: number;
  structure?: string;
  note?: TableNote;
  orphaned?: true;
}

/** Business note from the warehouse's tb_index. */
interface TableNote {
  /** Business meaning — what the list shows. */
  description: string;
  /** Joins, keys, traps, column remarks. */
  technical: string | null;
  open_question: string | null;
  source: "human" | "llm";
  updated_by: string | null;
  updated_at: string;
}

interface SchemaIndexResponse {
  ok: boolean;
  tables: SchemaIndexEntry[];
  warehouse_error?: string;
}

interface NeighborStub {
  name: string;
  category: SchemaCategory;
  group: string;
  column_count: number;
}

interface TableDetail {
  table: SchemaTable & { structure?: string; note?: TableNote };
  relationships: SchemaRelationship[];
  neighbors: NeighborStub[];
  neighbor_overflow: number;
}

interface SchemaTablesResponse {
  ok: boolean;
  tables: TableDetail[];
  missing: string[];
  warehouse_error?: string;
}

const CATEGORY_BADGE: Record<SchemaCategory, { label: string; className: string }> = {
  raw: { label: "raw", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  pivot: { label: "pivot", className: "bg-slate-100 text-slate-700 border-slate-200" },
  schema: { label: "schema", className: "bg-violet-100 text-violet-800 border-violet-200" },
  other: { label: "other", className: "bg-zinc-100 text-zinc-700 border-zinc-200" },
};

export const Route = createFileRoute("/_authed/$companySlug/schema")({
  validateSearch: (s: Record<string, unknown>) => ({
    table: typeof s.table === "string" ? s.table : undefined,
  }),
  beforeLoad: ({ context, params }) => {
    requireAdmin(context, params.companySlug);
  },
  component: CompanySchemaPage,
});

function CompanySchemaPage() {
  const { company } = Route.useRouteContext();
  const { companySlug } = Route.useParams();
  const { table } = Route.useSearch();
  const nav = useNavigate({ from: "/$companySlug/schema" });
  const [search, setSearch] = useState("");
  const [openOnly, setOpenOnly] = useState(false);

  // Navigate to (or clear) the focused table via the URL search param so the
  // view is bookmarkable / back-button friendly.
  const setTable = (name: string | undefined) =>
    nav({ to: "/$companySlug/schema", params: { companySlug }, search: { table: name } });

  // The catalog: compact index of every table (names + counts, no columns).
  const indexQ = useQuery({
    queryKey: ["schema-index", company.id],
    queryFn: () => api<SchemaIndexResponse>(`/api/tenants/${company.id}/schema-index`),
    staleTime: 30_000,
  });

  // Lazy per-table detail: only fetched when a table is focused. Bounded to
  // the focus table + its immediate neighbors.
  const detailQ = useQuery({
    queryKey: ["schema-table", company.id, table],
    queryFn: () =>
      api<SchemaTablesResponse>(`/api/tenants/${company.id}/schema-tables`, {
        method: "POST",
        body: JSON.stringify({ tables: [table] }),
      }),
    enabled: !!table,
    staleTime: 30_000,
  });

  const fuse = useMemo(() => {
    if (!indexQ.data?.tables) return null;
    return new Fuse(indexQ.data.tables, {
      keys: ["name", "group", "structure", "note.description", "note.technical"],
      includeMatches: true,
      includeScore: true,
      ignoreLocation: true,
      threshold: 0.3,
      minMatchCharLength: 2,
    });
  }, [indexQ.data]);

  // Grouped, optionally-filtered catalog for the landing view.
  const catalog = useMemo(() => {
    const all = indexQ.data?.tables ?? [];
    const matchesByName = new Map<string, readonly FuseResultMatch[]>();
    const q = search.trim();
    let entries: SchemaIndexEntry[];
    if (fuse && q) {
      const results = fuse.search(q);
      entries = results.map((r) => {
        matchesByName.set(r.item.name, r.matches ?? []);
        return r.item;
      });
    } else {
      entries = all;
    }
    if (openOnly) entries = entries.filter((e) => e.note?.open_question);
    const groups = new Map<string, SchemaIndexEntry[]>();
    for (const e of entries) {
      const bucket = groups.get(e.group) ?? [];
      bucket.push(e);
      groups.set(e.group, bucket);
    }
    const grouped = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    return { grouped, matchesByName, topMatch: entries[0]?.name, total: entries.length };
  }, [indexQ.data, fuse, search, openOnly]);
  const openCount = (indexQ.data?.tables ?? []).filter((e) => e.note?.open_question).length;

  const focused = typeof table === "string" && table.length > 0;

  return (
    <PageContent>
      <div className="pt-4 pb-8 md:pt-6 space-y-4">
        <PageHeader
            onBack={focused ? () => setTable(undefined) : undefined}
            title={focused ? table : "Warehouse schema"}
            subtitle={!focused ? <>Live view of tables + foreign keys, read from <code>information_schema</code> on every load. Browse the catalog, then open a table to see its columns and immediate relationships — click a neighbor to re-center.</> : undefined}
            actions={
              <div className="flex flex-wrap items-center justify-end gap-2">
            {!focused && openCount > 0 ? (
              <FilterChip active={openOnly} onClick={() => setOpenOnly(!openOnly)} label={`Open questions (${openCount})`} />
            ) : null}
            {!focused ? (
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                <input
                  type="search"
                  placeholder="Search tables…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && catalog.topMatch) {
                      e.preventDefault();
                      setTable(catalog.topMatch);
                    } else if (e.key === "Escape") {
                      setSearch("");
                    }
                  }}
                  className="w-72 rounded-md border border-input bg-background pl-8 pr-8 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                />
                {search ? (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => setSearch("")}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5"
                  >
                    <X className="size-4" />
                  </button>
                ) : null}
              </div>
            ) : null}
            <Button asChild variant="outline" title="Download schema as Markdown for LLM ingestion">
              <a
                href={`/api/tenants/${company.id}/schema-graph.md`}
                target="_blank"
                rel="noreferrer"
              >
                <Download className="size-4" />
                Export .md
              </a>
            </Button>
            <Button
              variant="outline"
              onClick={() => (focused ? detailQ.refetch() : indexQ.refetch())}
            >
              <RefreshCw
                className={`size-4 ${
                  (focused ? detailQ.isFetching : indexQ.isFetching) ? "animate-spin" : ""
                }`}
              />
              Refresh
            </Button>
          </div>
            }
          />

        {focused ? (
          <FocusedView
            companyId={company.id}
            table={table}
            detailQ={detailQ}
            onOpenTable={(t) => setTable(t)}
            onBackToCatalog={() => setTable(undefined)}
          />
        ) : (
          <CatalogView indexQ={indexQ} catalog={catalog} onOpenTable={(t) => setTable(t)} />
        )}
      </div>
    </PageContent>
  );
}

// ---------------------------------------------------------------------------
// Catalog landing
// ---------------------------------------------------------------------------

function CatalogView({
  indexQ,
  catalog,
  onOpenTable,
}: {
  indexQ: UseQueryResult<SchemaIndexResponse>;
  catalog: {
    grouped: [string, SchemaIndexEntry[]][];
    matchesByName: Map<string, readonly FuseResultMatch[]>;
    topMatch: string | undefined;
    total: number;
  };
  onOpenTable: (name: string) => void;
}) {
  if (indexQ.isLoading || (!indexQ.data && indexQ.isFetching)) {
    return <InfoCard tone="muted">Loading catalog…</InfoCard>;
  }
  if (indexQ.error) {
    return <InfoCard tone="destructive">Failed to load the schema catalog.</InfoCard>;
  }
  const data = indexQ.data;
  if (!data) return <InfoCard tone="muted">Loading catalog…</InfoCard>;

  return (
    <>
      {data.warehouse_error ? <WarehouseErrorCard message={data.warehouse_error} /> : null}

      {!data.ok || data.tables.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No tables yet. Run a sync job for this company to initialise the
            warehouse.
          </CardContent>
        </Card>
      ) : catalog.total === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No tables match your search.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="text-sm text-muted-foreground">
            {catalog.total} table{catalog.total === 1 ? "" : "s"} · click a row to open its
            relationships
          </div>
          <div className="space-y-6">
            {catalog.grouped.map(([group, entries]) => (
              <div key={group}>
                <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  {group}{" "}
                  <span className="font-normal normal-case">({entries.length})</span>
                </h2>
                <div className="rounded-md border divide-y">
                  {entries.map((entry) => (
                    <button
                      key={entry.name}
                      type="button"
                      onClick={() => onOpenTable(entry.name)}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-accent transition-colors"
                    >
                      <span className="flex-1 min-w-0">
                        <span className="font-mono text-sm truncate block">
                          {highlightMatch(entry.name, catalog.matchesByName.get(entry.name) ?? [])}
                          {entry.note?.open_question ? (
                            <span className="ml-2 text-amber-600" title={entry.note.open_question}>?</span>
                          ) : null}
                          {entry.orphaned ? <span className="ml-2 text-xs text-muted-foreground">(table gone)</span> : null}
                        </span>
                        {entry.note ? (
                          <span className="text-xs text-muted-foreground line-clamp-2 block">
                            {entry.note.description}
                          </span>
                        ) : null}
                      </span>
                      <CategoryBadge category={entry.category} />
                      <span className="text-xs text-muted-foreground whitespace-nowrap w-20 text-right">
                        {entry.column_count} col{entry.column_count === 1 ? "" : "s"}
                      </span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap w-24 text-right">
                        {entry.row_estimate >= 0
                          ? `~${Intl.NumberFormat().format(entry.row_estimate)} rows`
                          : "— rows"}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Focused bounded ERD
// ---------------------------------------------------------------------------

function FocusedView({
  companyId,
  table,
  detailQ,
  onOpenTable,
  onBackToCatalog,
}: {
  companyId: string;
  table: string;
  detailQ: UseQueryResult<SchemaTablesResponse>;
  onOpenTable: (name: string) => void;
  onBackToCatalog: () => void;
}) {


  if (detailQ.isLoading || (!detailQ.data && detailQ.isFetching)) {
    return (
      <>
        <InfoCard tone="muted">Loading {table}…</InfoCard>
      </>
    );
  }
  if (detailQ.error) {
    return (
      <>
        <InfoCard tone="destructive">Failed to load {table}.</InfoCard>
      </>
    );
  }
  const data = detailQ.data;
  if (!data) {
    return (
      <>
        <InfoCard tone="muted">Loading {table}…</InfoCard>
      </>
    );
  }
  if (data.warehouse_error) {
    return (
      <>
        <WarehouseErrorCard message={data.warehouse_error} />
      </>
    );
  }

  const detail = data.tables[0];
  if (!detail) {
    return (
      <>
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Table <code className="font-mono">{table}</code> was not found in the
            warehouse.{" "}
            <button
              type="button"
              onClick={onBackToCatalog}
              className="text-primary hover:underline"
            >
              Open catalog
            </button>
          </CardContent>
        </Card>
      </>
    );
  }

  const relationships = detail.relationships.map(normalizeRelationship);

  // Neighbors render with ONLY their join columns — the ones the edges
  // actually connect to. This is load-bearing, not cosmetic: SchemaDiagram
  // anchors every edge to per-column Handles (`<col>__l` / `<col>__r`), so a
  // neighbor with no columns has no handles and React Flow silently drops the
  // edge — the ERD then shows boxes with no connections at all. Keeping just
  // the key columns preserves the bounded/cheap render while making the join
  // visible.
  const joinColumnsByTable = new Map<string, Set<string>>();
  for (const r of relationships) {
    for (const [tbl, cols] of [
      [r.from_table, r.from_columns],
      [r.to_table, r.to_columns],
    ] as const) {
      let set = joinColumnsByTable.get(tbl);
      if (!set) {
        set = new Set<string>();
        joinColumnsByTable.set(tbl, set);
      }
      for (const c of cols) set.add(c);
    }
  }

  const diagramTables: SchemaTable[] = [
    detail.table,
    ...detail.neighbors.map<SchemaTable>((n) => ({
      schema: "public",
      name: n.name,
      row_count: null,
      columns: [...(joinColumnsByTable.get(n.name) ?? [])].map<SchemaColumn>((name) => ({
        name,
        data_type: "",
        is_nullable: true,
        is_primary_key: false,
        is_foreign_key: true,
      })),
    })),
  ];

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div className="text-sm text-muted-foreground">
          <code className="font-mono text-foreground">{detail.table.name}</code> ·{" "}
          {detail.table.columns.length} columns · {detail.neighbors.length} neighbor
          {detail.neighbors.length === 1 ? "" : "s"}
        </div>
      </div>

      {detail.neighbor_overflow > 0 ? (
        <div className="text-sm text-muted-foreground">
          +{detail.neighbor_overflow} more table
          {detail.neighbor_overflow === 1 ? "" : "s"} reference this —{" "}
          <button
            type="button"
            onClick={onBackToCatalog}
            className="text-primary hover:underline"
          >
            search the catalog
          </button>
        </div>
      ) : null}

      {/* key: the editor seeds its text from the note once; a new table needs a fresh editor. */}
      <NoteEditor key={detail.table.name} companyId={companyId} table={detail.table.name} structure={detail.table.structure} note={detail.table.note} />

      <SchemaDiagram
        tables={diagramTables}
        relationships={relationships}
        focused={table}
        onFocusChange={(t) => onOpenTable(t ?? "")}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Table note (tb_index) editor
// ---------------------------------------------------------------------------

function NoteEditor({
  companyId,
  table,
  structure,
  note,
}: {
  companyId: string;
  table: string;
  structure?: string;
  note?: TableNote;
}) {
  const qc = useQueryClient();
  const [description, setDescription] = useState(note?.description ?? "");
  const [technical, setTechnical] = useState(note?.technical ?? "");
  const [question, setQuestion] = useState(note?.open_question ?? "");
  const save = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; warehouse_error?: string }>(`/api/tenants/${companyId}/table-note`, {
        method: "PUT",
        body: JSON.stringify({ table, description, technical, openQuestion: question }),
      }),
    onSuccess: (r) => {
      if (r.ok) {
        qc.invalidateQueries({ queryKey: ["schema-index", companyId] });
        qc.invalidateQueries({ queryKey: ["schema-table", companyId, table] });
      }
    },
  });
  const dirty =
    description !== (note?.description ?? "") ||
    technical !== (note?.technical ?? "") ||
    question !== (note?.open_question ?? "");
  const error = save.error ? String(save.error) : save.data && !save.data.ok ? save.data.warehouse_error : undefined;
  return (
    <Card>
      <CardContent className="py-4 space-y-3">
        {structure ? <p className="text-sm text-muted-foreground">{structure}</p> : null}
        <label className="block text-sm font-medium">
          Business notes
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            placeholder="What this table means for the business: who uses it, what process it tracks, quirks, column remarks…"
            className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm font-normal"
          />
        </label>
        <label className="block text-sm font-medium">
          Technical notes
          <textarea
            value={technical}
            onChange={(e) => setTechnical(e.target.value)}
            rows={5}
            placeholder="Joins, keys, traps, column remarks…"
            className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm font-normal font-mono"
          />
        </label>
        <label className="block text-sm font-medium">
          Open question
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={2}
            placeholder="Anything still unclear (leave empty when resolved)"
            className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm font-normal"
          />
        </label>
        <div className="flex items-center gap-3">
          <Button onClick={() => save.mutate()} disabled={!dirty || !description.trim() || save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
          {note ? (
            <span className="text-xs text-muted-foreground">
              {note.source === "llm" ? "Written by the LLM" : "Written by a person"}
              {note.updated_by ? ` · ${note.updated_by}` : ""} · {new Date(note.updated_at).toLocaleString()}
            </span>
          ) : null}
          {error ? <span className="text-xs text-destructive">{error}</span> : null}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function CategoryBadge({ category }: { category: SchemaCategory }) {
  const b = CATEGORY_BADGE[category];
  return (
    <span
      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border font-medium ${b.className}`}
    >
      {b.label}
    </span>
  );
}

function InfoCard({
  tone,
  children,
}: {
  tone: "muted" | "destructive";
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent
        className={`py-10 text-center ${
          tone === "destructive" ? "text-destructive" : "text-muted-foreground"
        }`}
      >
        {children}
      </CardContent>
    </Card>
  );
}

function WarehouseErrorCard({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="font-medium text-warning">Warehouse unreachable</div>
        <div className="text-sm text-muted-foreground mt-1 font-mono break-words">
          {message}
        </div>
      </CardContent>
    </Card>
  );
}

// Kept from the previous implementation — a chip for boolean-ish filters.
function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-3 py-1 rounded-full border text-sm transition-colors " +
        (active
          ? "bg-foreground text-background border-foreground"
          : "bg-card text-foreground border-input hover:bg-accent")
      }
    >
      {label}
    </button>
  );
}

function highlightMatch(
  name: string,
  matches: readonly FuseResultMatch[],
): React.ReactNode {
  const ranges: Array<[number, number]> = [];
  for (const m of matches) {
    if (m.key !== "name") continue;
    for (const [a, b] of m.indices) ranges.push([a, b]);
  }
  if (ranges.length === 0) return name;
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1] + 1) {
      last[1] = Math.max(last[1], r[1]);
    } else {
      merged.push([r[0], r[1]]);
    }
  }
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  merged.forEach(([a, b], i) => {
    if (a > cursor) parts.push(name.slice(cursor, a));
    parts.push(
      <mark key={i} className="bg-warning rounded-sm">
        {name.slice(a, b + 1)}
      </mark>,
    );
    cursor = b + 1;
  });
  if (cursor < name.length) parts.push(name.slice(cursor));
  return <>{parts}</>;
}

function normalizeRelationship(r: SchemaRelationship): SchemaRelationship {
  return {
    ...r,
    from_columns: asArray(r.from_columns),
    to_columns: asArray(r.to_columns),
  };
}
function asArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === "string") {
    const m = /^\{(.*)\}$/.exec(v);
    const inner = m?.[1];
    if (inner !== undefined)
      return inner.length ? inner.split(",").map((s) => s.replace(/^"|"$/g, "")) : [];
    return v ? [v] : [];
  }
  return [];
}

// FilterChip is retained for future filter UI; reference it so linters that
// flag unused exports stay quiet without changing behaviour.
void FilterChip;
